// BUILD-042 cycle 12 - personal phone: paste-friendly formatting and validation. UI ONLY: no stored value is ever migrated and nothing is reformatted on load.
//
// Rules (Owner decision, "Employee details and two known save gaps"):
//   * empty is allowed (the phone is optional);
//   * US numbers - 10 digits, 11 digits starting with 1, or "+1" followed by 10 digits - become "(555) 123-4567" when the person EDITS the field;
//   * any other number that starts with "+" (international) is accepted exactly as typed;
//   * a value the person did not edit (the stored value) is never touched or judged, even if it would not pass today's rules;
//   * anything else is invalid: the screen shows a plain message and blocks Save & continue without discarding the edit.
// Pure functions, tested under plain Node (phone.test.mjs).

export const PHONE_INVALID_MESSAGE = 'Enter a 10-digit US number such as (555) 123-4567, or an international number that starts with + and the country code.';

export type PhoneCheck =
  | { ok: true; /** What to store/show: '' when empty, the US format, or the international text as typed (trimmed). */ value: string; kind: 'empty' | 'us' | 'international' }
  | { ok: false; message: string };

const digitsOf = (s: string) => s.replace(/\D/g, '');
const US_FORMAT = (d10: string) => `(${d10.slice(0, 3)}) ${d10.slice(3, 6)}-${d10.slice(6)}`;

/** Classify what the person typed or pasted. */
export function checkPhone(raw: string | null | undefined): PhoneCheck {
  const text = (raw ?? '').trim();
  if (!text) return { ok: true, value: '', kind: 'empty' };
  // Only phone punctuation may appear: digits, spaces, ( ) - . and a single leading +. Letters, extensions ("x12") and other symbols are not accepted.
  if (!/^\+?[0-9\s().-]+$/.test(text)) return { ok: false, message: PHONE_INVALID_MESSAGE };
  const digits = digitsOf(text);
  if (text.startsWith('+')) {
    if (digits.startsWith('1')) {
      return digits.length === 11 ? { ok: true, value: US_FORMAT(digits.slice(1)), kind: 'us' } : { ok: false, message: PHONE_INVALID_MESSAGE };
    }
    // International: accepted untouched. E.164 allows at most 15 digits; fewer than 7 cannot be a dialable number.
    return digits.length >= 7 && digits.length <= 15 ? { ok: true, value: text, kind: 'international' } : { ok: false, message: PHONE_INVALID_MESSAGE };
  }
  if (digits.length === 10) return { ok: true, value: US_FORMAT(digits), kind: 'us' };
  if (digits.length === 11 && digits.startsWith('1')) return { ok: true, value: US_FORMAT(digits.slice(1)), kind: 'us' };
  return { ok: false, message: PHONE_INVALID_MESSAGE };
}

const same = (a: string | null | undefined, b: string | null | undefined) => (a ?? '').trim() === (b ?? '').trim();

/**
 * What a save would write for the phone, given the text in the field and the stored value.
 * An UNEDITED field returns the stored value untouched (never reformatted, never judged); an edited one is checked and normalized.
 */
export function phoneForSave(entered: string | null | undefined, stored: string | null | undefined): { ok: true; value: string | null; changed: boolean } | { ok: false; message: string } {
  if (same(entered, stored)) return { ok: true, value: (stored ?? '').trim() ? (stored as string) : null, changed: false };
  const c = checkPhone(entered);
  if (!c.ok) return c;
  const value = c.value === '' ? null : c.value;
  return { ok: true, value, changed: !same(value, stored) };
}

/** The message to show/block with while the field holds an EDITED value that is not acceptable; null when fine or unedited. */
export function phoneProblem(entered: string | null | undefined, stored: string | null | undefined): string | null {
  const r = phoneForSave(entered, stored);
  return r.ok ? null : r.message;
}

/** Paste/blur helper: the text to put back in the field after an edit (formatted when valid, exactly what was typed when not). Unedited text is returned as-is. */
export function formatEdited(entered: string, stored: string | null | undefined): string {
  if (same(entered, stored)) return entered;
  const c = checkPhone(entered);
  return c.ok ? c.value : entered;
}
